import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Calendar, CheckCircle, XCircle, RefreshCw, ExternalLink } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';

interface GoogleCalendarStatus {
  connected: boolean;
  integration?: {
    google_email: string;
    connected_at: string;
  };
}

export const GoogleCalendarIntegration: React.FC = () => {
  const { toast } = useToast();
  const { t } = useTranslation('dashboard');
  const [googleStatus, setGoogleStatus] = useState<GoogleCalendarStatus>({ connected: false });
  const [isLoading, setIsLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  const fetchGoogleStatus = async () => {
    try {
      console.log('🔄 Fetching Google Calendar status...');
      
      // Get current session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session?.access_token) {
        console.log('⚠️ No valid session found, attempting refresh...');
        const { data: { session: refreshedSession }, error: refreshError } = await supabase.auth.refreshSession();
        
        if (refreshError || !refreshedSession?.access_token) {
          setIsLoading(false);
          toast({
            title: 'Authentication Required',
            description: 'Please log in to check Google Calendar status',
            variant: "destructive",
          });
          return;
        }
        
        console.log('✅ Session refreshed successfully');
        
        const { data, error } = await supabase.functions.invoke('google-calendar-status', {
          headers: {
            Authorization: `Bearer ${refreshedSession.access_token}`
          }
        });

        if (error) {
          console.error('❌ Google Calendar status error:', error);
          // Check if error indicates token expiration
          if (error.message?.includes('expired') || error.message?.includes('reauth_required')) {
            setGoogleStatus({ connected: false });
            toast({
              title: 'Google Calendar Expired',
              description: 'Your Google Calendar connection has expired. Please reconnect.',
              variant: "destructive",
            });
            return;
          }
          throw error;
        }

        console.log('✅ Google Calendar status received:', data);
        setGoogleStatus(data);
      } else {
        console.log('✅ Valid session found, checking status...');
        
        const { data, error } = await supabase.functions.invoke('google-calendar-status', {
          headers: {
            Authorization: `Bearer ${session.access_token}`
          }
        });

        if (error) {
          console.error('❌ Google Calendar status error:', error);
          // Check if error indicates token expiration
          if (error.message?.includes('expired') || error.message?.includes('reauth_required')) {
            setGoogleStatus({ connected: false });
            toast({
              title: 'Google Calendar Expired',
              description: 'Your Google Calendar connection has expired. Please reconnect.',
              variant: "destructive",
            });
            return;
          }
          throw error;
        }

        console.log('✅ Google Calendar status received:', data);
        setGoogleStatus(data);
      }
    } catch (error) {
      console.error('Error fetching Google Calendar status:', error);
      const errorMessage = error?.message || 'Failed to check Google Calendar status';
      toast({
        title: 'Connection Error',
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchGoogleStatus();
  }, []);

  const handleConnect = async () => {
    setIsConnecting(true);
    
    try {
      // Generate CSRF state token
      const state = crypto.randomUUID();
      sessionStorage.setItem('gc_oauth_state', state);
      
      // Store current page for redirect back
      sessionStorage.setItem('gc_return_url', window.location.href);

      // Get auth URL from edge function with state parameter
      const { data: authData, error: authError } = await supabase.functions.invoke('google-calendar-auth', {
        body: { 
          action: 'get_auth_url',
          state: state
        }
      });

      if (authError) {
        throw new Error(`Failed to get auth URL: ${authError.message}`);
      }

      console.log('🔗 Redirecting to Google OAuth URL');
      console.log('🌐 Current domain:', window.location.hostname);
      console.log('🔑 State stored:', state);
      
      // Redirect to Google OAuth (similar to Google login)
      window.location.href = authData.auth_url;

    } catch (error) {
      console.error('❌ Error connecting Google Calendar:', error);
      toast({
        title: 'Connection Failed',
        description: error instanceof Error ? error.message : 'Failed to connect Google Calendar',
        variant: "destructive",
      });
      setIsConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    setIsDisconnecting(true);
    try {
      console.log('🔄 Disconnecting Google Calendar...');
      
      // Ensure valid session
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Please log in to disconnect Google Calendar');
      }
      
      const { data, error } = await supabase.functions.invoke('google-calendar-auth', {
        body: { action: 'disconnect' },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      if (error) {
        console.error('❌ Disconnect error:', error);
        throw error;
      }

      console.log('✅ Google Calendar disconnected successfully');
      setGoogleStatus({ connected: false });
      toast({
        title: 'Disconnected',
        description: 'Your Google Calendar has been disconnected successfully',
      });
    } catch (error) {
      console.error('Error disconnecting Google Calendar:', error);
      toast({
        title: 'Disconnection Failed',
        description: 'Unable to disconnect Google Calendar',
        variant: "destructive",
      });
    } finally {
      setIsDisconnecting(false);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Google Calendar Integration
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center p-8">
            <RefreshCw className="w-6 h-6 animate-spin" />
            <span className="ml-2">Loading...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="w-5 h-5" />
          Google Calendar Integration
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Connection Status */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3">
            <div className="flex items-center gap-2">
              {googleStatus.connected ? (
                <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
              ) : (
                <XCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
              )}
              <span className="font-medium text-sm sm:text-base">
                {googleStatus.connected ? 'Connected' : 'Not Connected'}
              </span>
            </div>
            <Badge variant={googleStatus.connected ? 'default' : 'secondary'} className="text-xs">
              {googleStatus.connected ? 'Active' : 'Inactive'}
            </Badge>
          </div>

          {googleStatus.connected ? (
            <Button
              onClick={handleDisconnect}
              disabled={isDisconnecting}
              variant="outline"
              size="sm"
            >
              {isDisconnecting ? (
                <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              {isDisconnecting ? 'Disconnecting...' : 'Disconnect'}
            </Button>
          ) : (
            <Button
              onClick={handleConnect}
              disabled={isConnecting}
              size="sm"
            >
              {isConnecting ? (
                <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              {isConnecting ? 'Connecting...' : 'Connect'}
            </Button>
          )}
        </div>

        {/* Connection Details */}
        {googleStatus.connected && googleStatus.integration && (
          <>
            <Separator />
            <div className="space-y-3">
              <h4 className="font-medium text-sm sm:text-base">Integration Details</h4>
              <div className="space-y-2 text-xs sm:text-sm">
                <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0">
                  <span className="text-muted-foreground">Email:</span>
                  <span className="break-all">{googleStatus.integration.google_email}</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0">
                  <span className="text-muted-foreground">Connected At:</span>
                  <span className="text-xs sm:text-sm">
                    {new Date(googleStatus.integration.connected_at).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Features */}
        <Separator />
        <div className="space-y-3">
          <h4 className="font-medium text-sm sm:text-base">Features</h4>
          <ul className="space-y-2 sm:space-y-3 text-xs sm:text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-green-600 mt-0.5 flex-shrink-0" />
              <span>Automatically create calendar events for appointments</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-green-600 mt-0.5 flex-shrink-0" />
              <span>Generate Google Meet links for virtual sessions</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-green-600 mt-0.5 flex-shrink-0" />
              <span>Send meeting invitations to clients automatically</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-green-600 mt-0.5 flex-shrink-0" />
              <span>Sync appointment updates to your calendar</span>
            </li>
          </ul>
        </div>

        {!googleStatus.connected && (
          <>
            <Separator />
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
              <div className="flex items-start gap-2 sm:gap-3">
                <ExternalLink className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-xs sm:text-sm">
                  <p className="font-medium text-blue-900 mb-1">Connect Google Calendar</p>
                  <p className="text-blue-800">
                    Connect your Google Calendar to automatically create events and Google Meet links for your appointments.
                  </p>
                </div>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};